import type { TokenOrigin } from '@/components/timetable-import/typesV2'

/**
 * Generate a stable origin key for matching across pages.
 * Excludes row_index, col_index, adjacent_tokens (vary per page).
 */
export function getOriginKey(origin: TokenOrigin | null | undefined): string {
  if (!origin) return 'unknown'
  const parts: string[] = [origin.source]

  if (origin.context?.row_label_type) parts.push(`row:${origin.context.row_label_type}`)
  if (origin.context?.lesson_time_slot) parts.push(`time:${origin.context.lesson_time_slot}`)
  if (origin.context?.lesson_number !== undefined) parts.push(`lesson:${origin.context.lesson_number}`)
  if (origin.context?.header_section) parts.push(`header:${origin.context.header_section}`)
  if (origin.context?.day_header) parts.push(`day:${origin.context.day_header}`)

  return parts.join('|')
}

/**
 * Get the compound parent key for fallback matching.
 */
export function getCompoundParentKey(origin: TokenOrigin | null | undefined): string | null {
  if (!origin?.structural?.compound_parent) return null
  return `compound:${origin.structural.compound_parent}`
}

/**
 * Check if a token should be auto-confirmed based on origin matching.
 * Checks: exact origin match, compound parent match, related subject match.
 */
export function shouldAutoConfirmByOrigin(
  token: string,
  origin: TokenOrigin | null,
  knownTokens: Record<string, { confirmed: boolean; confirmedField?: string | null; field?: string; originKey?: string | null }>,
  sessionConfirmations: Record<string, { confirmed: boolean; confirmedField: string | null }>
): boolean {
  if (!origin) return false

  const originKey = getOriginKey(origin)
  const compoundKey = getCompoundParentKey(origin)

  // 1. Exact origin match in session confirmations
  const sessionKey = `${token}::${originKey}`
  if (sessionConfirmations[sessionKey]?.confirmed) return true

  // 2. Exact origin match in known tokens (from backend template)
  if (knownTokens[token]?.originKey === originKey && knownTokens[token]?.confirmed) return true

  // 3. Compound parent confirmed — if parent is confirmed, parts are auto-resolved
  if (compoundKey) {
    if (knownTokens[compoundKey]?.confirmed) return true
    if (sessionConfirmations[compoundKey]?.confirmed) return true
  }

  // 4. Related subject confirmed
  if (origin.structural?.related_subject) {
    const subject = knownTokens[origin.structural.related_subject]
    if (subject?.confirmed) return true
    const subjectSession = sessionConfirmations[origin.structural.related_subject]
    if (subjectSession?.confirmed) return true
  }

  return false
}

/**
 * Get human-readable label for origin.
 */
export function getOriginLabel(origin: TokenOrigin | null | undefined): string {
  if (!origin) return ''

  const sourceLabels: Record<string, string> = {
    header: 'Header',
    row_label: 'Zeile',
    cell_content: 'Zelle',
    cell_time: 'Zeit',
    filename: 'Datei',
    markdown: 'Markdown',
    template: 'Template',
    derived: 'Erkannt',
  }

  const parts: string[] = [sourceLabels[origin.source] || origin.source]

  if (origin.context?.row_label_type === 'lesson_number_and_time' && origin.context.lesson_number !== undefined) {
    parts.push(`L${origin.context.lesson_number}`)
  }
  if (origin.context?.lesson_time_slot) {
    parts.push(origin.context.lesson_time_slot)
  }
  if (origin.context?.header_section) {
    parts.push(origin.context.header_section)
  }
  if (origin.structural?.compound_parent) {
    parts.push(`aus ${origin.structural.compound_parent}`)
  }

  return parts.join(' · ')
}

/**
 * Get detailed human-readable description for tooltip.
 */
export function getOriginDescription(origin: TokenOrigin | null | undefined): string {
  if (!origin) return 'Unbekannte Herkunft'

  const parts: string[] = []

  switch (origin.source) {
    case 'header':
      parts.push(`Extrahiert aus PDF-Header (${origin.context?.header_section || 'unbekannt'})`)
      break
    case 'row_label':
      parts.push(`Extrahiert aus Zeilenbeschriftung`)
      if (origin.context?.row_label_type === 'lesson_number_and_time') {
        parts.push(`Lektion ${origin.context.lesson_number} um ${origin.context.lesson_time_slot}`)
      } else if (origin.context?.row_label_type === 'lesson_number_only') {
        parts.push(`Lektion ${origin.context.lesson_number}`)
      }
      break
    case 'cell_content':
      parts.push(`Extrahiert aus Zelleninhalt`)
      if (origin.context?.day_header) parts.push(`Tag: ${origin.context.day_header}`)
      if (origin.context?.time_slot) parts.push(`Zeit: ${origin.context.time_slot}`)
      if (origin.context?.lesson_number !== undefined) parts.push(`Lektion: ${origin.context.lesson_number}`)
      break
    case 'cell_time':
      parts.push(`Extrahiert aus Zellen-Zeitfeld`)
      break
    case 'template':
      parts.push('Aus Template übernommen (bereits bestätigt)')
      break
    case 'derived':
      parts.push('Durch Validator/Pattern erkannt')
      break
    default:
      parts.push(`Quelle: ${origin.source}`)
  }

  if (origin.structural?.compound_parent) {
    parts.push(`Teil von: ${origin.structural.compound_parent}`)
  }
  if (origin.structural?.related_subject) {
    parts.push(`Zugehöriges Fach: ${origin.structural.related_subject}`)
  }

  return parts.join('\n')
}
